import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  MapPin,
  Loader2,
  Phone,
  Clock,
  DollarSign,
  Heart,
  AlertCircle,
  Navigation,
  X,
  Landmark,
  UtensilsCrossed,
  BedDouble,
  Play,
  ChevronDown,
  ChevronUp,
  Check,
  ExternalLink,
} from "lucide-react";
import MainLayout from "../layouts/MainLayout";
import SEO from "../components/SEO";
import { locationService } from "../services/locationService";
import { useAppContext } from "../store/AppContext";
import { hasUsableImageUrl } from "../lib/placeholderImages";
import { sanitizeHtml } from "../lib/sanitizeHtml";
import {
  formatLocationAddress,
  hasFormattedAddress,
} from "../lib/locationAddress";
import { useAuth } from "../contexts/AuthContext";
import { isFoodCategoryDisplayName } from "../lib/foodCategoryDisplay";

function normalizeAsset(a) {
  if (!a) return null;

  if (typeof a === "string") {
    return {
      key: "",
      url: a,
      mimeType: "",
      size: 0,
      alt: "",
    };
  }

  return {
    key: a.key || "",
    url: a.url || "",
    mimeType: a.mimeType || "",
    size: a.size || 0,
    alt: a.alt || "",
    ...a,
  };
}

function buildMediaBlocks(
  location,
  fallbackMainImage,
  fallbackGallery,
  fallbackVideos
) {
  const blocks = Array.isArray(location?.mediaBlocks)
    ? location.mediaBlocks
    : [];

  if (blocks.length > 0) {
    return blocks
      .map((b) => {
        if (!b || typeof b !== "object") return null;

        const type = String(b.type || "").toLowerCase();

        if (!["image", "video", "text"].includes(type)) return null;

        return {
          type,
          caption: b.caption || "",
          title: b.title || "",
          content: b.content || "",
          image: b.image ? normalizeAsset(b.image) : null,
          thumbnail: b.thumbnail ? normalizeAsset(b.thumbnail) : null,
          videoUrl: b.videoUrl || "",
          video: b.video || null,
        };
      })
      .filter(Boolean);
  }

  const out = [];

  if (fallbackMainImage) {
    out.push({
      type: "image",
      caption: "",
      image: {
        key: "",
        url: fallbackMainImage,
      },
    });
  }

  for (const u of fallbackGallery || []) {
    out.push({
      type: "image",
      caption: "",
      image: {
        key: "",
        url: u,
      },
    });
  }

  for (const v of fallbackVideos || []) {
    out.push({
      type: "video",
      title: v.title || "",
      caption: "",
      videoUrl: v.url || "",
      thumbnail: v.thumbnail?.url
        ? {
            key: "",
            url: v.thumbnail.url,
          }
        : null,
      video: v,
    });
  }

  return out;
}

function formatVnd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";

  return n.toLocaleString("vi-VN");
}

function getCategoryIcon(categoryName) {
  if (
    categoryName === "Văn Hoá & Di Tích" ||
    categoryName === "Du lịch văn hoá"
  ) {
    return Landmark;
  }

  if (isFoodCategoryDisplayName(categoryName)) {
    return UtensilsCrossed;
  }

  if (
    categoryName === "Homestay & Lưu trú" ||
    categoryName === "Homestay & khách sạn"
  ) {
    return BedDouble;
  }

  return MapPin;
}

const cardClass =
  "rounded-2xl border-2 border-[#d9d9d9] bg-white shadow-[0_16px_44px_rgba(80,58,28,0.10)] ring-1 ring-white/50";

const panelForest = "rounded-xl bg-[#3a5840] border border-[#2c4632]";
const panelTeal = "rounded-xl bg-[#3a6665] border border-[#2c5250]";
const panelTerra = "rounded-xl bg-[#8a4a38] border border-[#723c2c]";
const panelAmber = "rounded-xl bg-[#7a5c26] border border-[#624a1c]";
const panelBurgundy = "rounded-xl bg-[#6a3a4a] border border-[#56303e]";

const strongTitleClass =
  "text-[10px] font-extrabold uppercase tracking-[0.2em] text-white/65";

const strongBodyClass = "text-sm leading-7 text-white";

const FAQ_PANELS = [
  panelForest,
  panelTeal,
  panelTerra,
  panelAmber,
  panelBurgundy,
];

const LocationDetailPage = () => {
  const { locationSlug } = useParams();

  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [galleryModal, setGalleryModal] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const [mediaCatalogOpen, setMediaCatalogOpen] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const [videoModal, setVideoModal] = useState({
    open: false,
    url: "",
    title: "",
  });

  const { toggleFavorite, isFavorite } = useAppContext();
  const { token } = useAuth();

  const locationMongoId = useMemo(() => {
    if (!location) return "";

    // Favorites API + AppContext store IDs by Mongo `_id` (string).
    // Prefer `_id` to keep UI state consistent across pages.
    const raw = location._id ?? location.id;

    return raw != null ? String(raw).trim() : "";
  }, [location]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const data = await locationService.getLocationBySlug(locationSlug, {
          incView: false,
        });

        if (!mounted) return;

        setLocation(data || null);
      } catch (e) {
        if (!mounted) return;

        setError(e?.message || "Không thể tải địa điểm");
        setLocation(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [locationSlug, token]);

  const seo = useMemo(() => {
    const name = location?.name || "Địa điểm";

    return {
      title: (location?.seoTitle || "").trim() || `${name} – Đà Lạt`,
      description:
        (location?.seoDescription || "").trim() ||
        (location?.shortIntro || location?.description || "").trim() ||
        `Khám phá ${name} tại Đà Lạt.`,
      keywords: Array.isArray(location?.seoKeywords)
        ? location.seoKeywords.filter(Boolean)
        : [],
    };
  }, [location]);

  const jsonLd = useMemo(() => {
    const locSlug = locationSlug || "";

    return {
      "@context": "https://schema.org",
      "@type": "TouristAttraction",
      name: seo.title,
      description: seo.description,
      url: `https://longvongdalat.com/location/${encodeURIComponent(
        locSlug
      )}`,
      geo: {
        "@type": "GeoCoordinates",
        latitude: location?.latitude || 0,
        longitude: location?.longitude || 0,
      },
      ...(hasUsableImageUrl(location?.image?.url)
        ? {
            image: location.image.url,
          }
        : null),
    };
  }, [location, locationSlug, seo]);

  const isFav = Boolean(locationMongoId && isFavorite(locationMongoId));

  const mainImage = hasUsableImageUrl(location?.image?.url)
    ? location.image.url
    : "";

  const gallery = (location?.gallery || [])
    .filter((img) => hasUsableImageUrl(img.url))
    .map((img) => img.url);

  const videos = useMemo(() => location?.videos || [], [location?.videos]);

  const mediaBlocks = useMemo(
    () => buildMediaBlocks(location, mainImage, gallery, videos),
    [location, mainImage, gallery, videos]
  );

  const canInlinePlay = (url) => {
    const u = String(url || "")
      .trim()
      .toLowerCase();

    return (
      Boolean(u) &&
      (u.includes(".mp4") ||
        u.includes(".webm") ||
        u.includes(".mov") ||
        u.startsWith("blob:"))
    );
  };

  const openVideo = (url, title = "") => {
    const u = String(url || "").trim();

    if (!u) return;

    if (canInlinePlay(u)) {
      setVideoModal({
        open: true,
        url: u,
        title: String(title || "").trim(),
      });
      return;
    }

    window.open(u, "_blank", "noreferrer");
  };

  const heroItems = useMemo(() => {
    const items = [];
    const heroGallery = Array.isArray(location?.heroGallery)
      ? location.heroGallery
      : [];

    if (heroGallery.length > 0) {
      heroGallery.forEach((a) => {
        const u = a?.url || "";

        if (hasUsableImageUrl(u)) {
          items.push({
            url: u,
            caption: a?.alt || "",
          });
        }
      });
    } else {
      if (hasUsableImageUrl(mainImage)) {
        items.push({
          url: mainImage,
          caption: "",
        });
      }

      for (const g of gallery) {
        if (hasUsableImageUrl(g)) {
          items.push({
            url: g,
            caption: "",
          });
        }
      }
    }

    const seen = new Set();

    return items.filter((x) => {
      if (!x?.url || seen.has(x.url)) return false;

      seen.add(x.url);

      return true;
    });
  }, [location?.heroGallery, mainImage, gallery]);

  const heroSlides = useMemo(() => {
    const slides = [];

    heroItems.forEach((it) => {
      if (!hasUsableImageUrl(it?.url)) return;

      slides.push({
        type: "image",
        url: it.url,
        caption: it.caption || "",
      });
    });

    const seen = new Set(slides.map((s) => s.url));
    const blocks = Array.isArray(location?.mediaBlocks)
      ? location.mediaBlocks
      : [];

    const pushVideo = (u, poster, title) => {
      const url = String(u || "").trim();

      if (!url || seen.has(url)) return;

      seen.add(url);

      slides.push({
        type: "video",
        url,
        poster: hasUsableImageUrl(poster) ? String(poster).trim() : "",
        caption: String(title || "").trim(),
      });
    };

    for (const b of blocks) {
      if (String(b?.type || "").toLowerCase() !== "video") continue;

      pushVideo(
        b?.videoUrl || b?.video?.url,
        b?.thumbnail?.url || b?.video?.thumbnail?.url,
        b?.title || b?.caption
      );
    }

    (Array.isArray(videos) ? videos : []).forEach((v) => {
      pushVideo(v?.url, v?.thumbnail?.url, v?.title);
    });

    return slides;
  }, [heroItems, location?.mediaBlocks, videos]);

  const modalSlides = useMemo(() => {
    const slides = [];

    heroItems.forEach((it) => {
      if (hasUsableImageUrl(it?.url)) {
        slides.push({
          type: "image",
          url: it.url,
          caption: it.caption || "",
        });
      }
    });

    const seen = new Set(slides.map((s) => s.url));
    const blocks = Array.isArray(location?.mediaBlocks)
      ? location.mediaBlocks
      : [];

    const pushVideo = (u, poster, title) => {
      const url = String(u || "").trim();

      if (!url || seen.has(url)) return;

      seen.add(url);

      slides.push({
        type: "video",
        url,
        poster: hasUsableImageUrl(poster) ? String(poster).trim() : "",
        caption: String(title || "").trim(),
      });
    };

    for (const b of blocks) {
      if (String(b?.type || "").toLowerCase() !== "video") continue;

      pushVideo(
        b?.videoUrl || b?.video?.url,
        b?.thumbnail?.url || b?.video?.thumbnail?.url,
        b?.title || b?.caption
      );
    }

    (Array.isArray(videos) ? videos : []).forEach((v) => {
      pushVideo(v?.url, v?.thumbnail?.url, v?.title);
    });

    return slides;
  }, [heroItems, location?.mediaBlocks, videos]);

  const activeHeroSlide = useMemo(() => {
    const idx = Math.min(heroIndex, Math.max(0, heroSlides.length - 1));

    return heroSlides[idx] || null;
  }, [heroIndex, heroSlides]);

  const heroUrl = useMemo(() => {
    if (activeHeroSlide?.type === "image") {
      return String(activeHeroSlide.url || "").trim();
    }

    return "";
  }, [activeHeroSlide]);

  const coverUrlRaw = useMemo(() => {
    const u = location?.image?.url || "";

    return hasUsableImageUrl(u) ? u : "";
  }, [location?.image?.url]);

  const heroGalleryOnlyUrls = useMemo(() => {
    const hg = Array.isArray(location?.heroGallery)
      ? location.heroGallery
      : [];

    const out = [];

    for (const a of hg) {
      const u = a?.url || "";

      if (hasUsableImageUrl(u)) out.push(u);
    }

    return out;
  }, [location?.heroGallery]);

  const heroUrlsCatalog = useMemo(() => {
    if (heroGalleryOnlyUrls.length > 0) return heroGalleryOnlyUrls;

    const urls = heroItems
      .map((x) => x.url)
      .filter((u) => hasUsableImageUrl(u));

    if (!coverUrlRaw) return urls;

    return urls.filter((u) => u !== coverUrlRaw);
  }, [heroGalleryOnlyUrls, heroItems, coverUrlRaw]);

  const contentUrlsCatalog = useMemo(() => {
    const raw = Array.isArray(location?.mediaBlocks)
      ? location.mediaBlocks
      : [];

    if (raw.length > 0) {
      return raw
        .filter(
          (b) =>
            String(b?.type || "").toLowerCase() === "image" &&
            hasUsableImageUrl(b?.image?.url)
        )
        .map((b) => b.image.url);
    }

    const used = new Set();

    if (coverUrlRaw) used.add(coverUrlRaw);

    heroUrlsCatalog.forEach((u) => used.add(u));

    return gallery.filter((u) => !used.has(u));
  }, [location?.mediaBlocks, gallery, coverUrlRaw, heroUrlsCatalog]);

  const mediaCatalogSections = useMemo(() => {
    const sections = [];

    if (coverUrlRaw) {
      sections.push({
        key: "cover",
        title: "Ảnh bìa (cover)",
        urls: [coverUrlRaw],
      });
    }

    if (heroUrlsCatalog.length) {
      sections.push({
        key: "hero",
        title: "Slider ảnh bìa (hero)",
        urls: heroUrlsCatalog,
      });
    }

    if (contentUrlsCatalog.length) {
      sections.push({
        key: "content",
        title: "Ảnh nội dung",
        urls: contentUrlsCatalog,
      });
    }

    return sections;
  }, [coverUrlRaw, heroUrlsCatalog, contentUrlsCatalog]);

  const totalCatalogImages = useMemo(() => {
    const seen = new Set();

    mediaCatalogSections.forEach((s) => {
      s.urls.forEach((u) => seen.add(u));
    });

    return seen.size;
  }, [mediaCatalogSections]);

  useEffect(() => {
    if (!mediaCatalogOpen) return;

    const prev = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
    };
  }, [mediaCatalogOpen]);

  useEffect(() => {
    if (!mediaCatalogOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") setMediaCatalogOpen(false);
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [mediaCatalogOpen]);

  const locSlugStr = String(locationSlug || "");
  const seoUrl = `https://longvongdalat.com/location/${encodeURIComponent(
    locSlugStr
  )}`;

  const CategoryIcon = getCategoryIcon(location?.categoryName);

  useEffect(() => {
    setHeroIndex(0);
  }, [locationMongoId]);

  useEffect(() => {
    if (heroItems.length <= 1) return;

    const t = setInterval(() => {
      setHeroIndex((i) => (i + 1) % heroItems.length);
    }, 4500);

    return () => clearInterval(t);
  }, [heroItems.length]);

  return (
    <MainLayout>
      <SEO
        title={seo.title}
        description={seo.description}
        image={heroUrl || coverUrlRaw || undefined}
        url={seoUrl}
        jsonLd={jsonLd}
        keywords={seo.keywords}
      />

      <div className="min-h-screen bg-white pb-16">
        {loading ? (
          <div className="mx-auto w-full max-w-7xl px-4 pb-14 pt-6 lg:max-w-[88vw]">
            <div
              className={`${cardClass} flex items-center justify-center px-6 py-16`}
            >
              <Loader2 className="animate-spin text-[#3a5840]" size={28} />
              <span className="ml-3 text-sm font-medium text-[#5d6558]">
                Đang tải địa điểm...
              </span>
            </div>
          </div>
        ) : error ? (
          <div className="mx-auto w-full max-w-7xl px-4 pb-14 pt-6 lg:max-w-[88vw]">
            <div className="rounded-[28px] border border-red-200 bg-red-50 px-6 py-10 text-center shadow-sm">
              <AlertCircle size={32} className="mx-auto mb-3 text-red-300" />
              <p className="text-sm font-semibold text-red-700">{error}</p>
            </div>
          </div>
        ) : !location ? (
          <div className="mx-auto w-full max-w-7xl px-4 pb-14 pt-6 lg:max-w-[88vw]">
            <div className={`${cardClass} px-6 py-10 text-center`}>
              <MapPin size={32} className="mx-auto mb-3 text-[#b39f82]" />
              <p className="text-sm font-semibold text-[#465041]">
                Không tìm thấy địa điểm
              </p>
            </div>
          </div>
        ) : (
          <>
            <section className="mx-auto w-full max-w-7xl px-4 pb-4 pt-6 lg:max-w-[88vw]">
              <div className="flex items-start gap-4">
                <div className="min-w-0 flex-1 text-center">
                  <div
                    className="mx-auto mb-3 h-px w-24 bg-[#d6c6a7]"
                    aria-hidden
                  />

                  <div className="mb-2 inline-flex items-center gap-2 rounded-md border-2 border-dashed border-[#8a5c38] bg-white px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#8a5c38]">
                    <CategoryIcon size={12} />
                    {location.categoryName || "Địa điểm"}
                  </div>

                  <h1 className="font-faustina text-3xl font-extrabold leading-tight text-[#2a2010] drop-shadow-[0_1px_0_rgba(255,255,255,0.55)] sm:text-4xl">
                    {location.name}
                  </h1>
                </div>
              </div>
            </section>

            <section className="mx-auto w-full max-w-7xl px-4 pb-6 lg:max-w-[88vw]">
              <div className="grid gap-6 lg:grid-cols-2 lg:items-stretch">
                <div
                  className={`relative flex min-h-0 flex-col overflow-hidden ${cardClass} rounded-none`}
                >
                  <div
                    className="relative w-full bg-[#e2d4ba]"
                    style={{
                      height: "clamp(220px, 35vw, 420px)",
                    }}
                  >
                    {activeHeroSlide?.type === "image" && heroUrl ? (
                      <img
                        src={heroUrl}
                        alt={location.name}
                        className="absolute inset-0 h-full w-full object-cover"
                        onError={(e) => {
                          const img = e.currentTarget;

                          img.onerror = null;
                          img.style.display = "none";

                          const parent = img.parentElement;

                          if (parent) parent.setAttribute("data-noimg", "1");
                        }}
                      />
                    ) : activeHeroSlide?.type === "video" ? (
                      <button
                        type="button"
                        onClick={() =>
                          openVideo(
                            activeHeroSlide.url,
                            activeHeroSlide.caption || "Video"
                          )
                        }
                        className="absolute inset-0 block"
                        aria-label="Mở video"
                      >
                        <video
                          key={activeHeroSlide.url}
                          src={activeHeroSlide.url}
                          poster={activeHeroSlide.poster || undefined}
                          className="absolute inset-0 h-full w-full object-cover"
                          preload="metadata"
                          playsInline
                          muted
                        />

                        <div className="absolute inset-0 flex items-center justify-center bg-black/25 transition hover:bg-black/40">
                          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                            <Play
                              size={42}
                              className="translate-x-0.5 text-white"
                              fill="white"
                            />
                          </div>
                        </div>
                      </button>
                    ) : null}

                    {!activeHeroSlide ? (
                      <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
                        <span className="rounded-lg border border-[#d8cfbf] bg-[#fffdf8]/85 px-4 py-2 text-sm font-semibold text-[#6f776b]">
                          Ảnh đang cập nhật
                        </span>
                      </div>
                    ) : null}

                    <div className="hidden absolute inset-0 items-center justify-center px-6 text-center [data-noimg='1']:&flex">
                      <span className="rounded-lg border border-[#d8cfbf] bg-[#fffdf8]/85 px-4 py-2 text-sm font-semibold text-[#6f776b]">
                        Ảnh đang cập nhật
                      </span>
                    </div>

                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />

                    {heroSlides.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setHeroIndex(
                              (i) =>
                                (i - 1 + heroSlides.length) %
                                heroSlides.length
                            )
                          }
                          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-none border border-[#d7cab6] bg-[#fdf8ee]/95 p-2.5 text-[#5b4e38] shadow-md hover:bg-white"
                          aria-label="Ảnh trước"
                        >
                          <ChevronDown size={18} className="rotate-90" />
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            setHeroIndex((i) => (i + 1) % heroSlides.length)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-none border border-[#d7cab6] bg-[#fdf8ee]/95 p-2.5 text-[#5b4e38] shadow-md hover:bg-white"
                          aria-label="Ảnh sau"
                        >
                          <ChevronUp size={18} className="rotate-90" />
                        </button>

                        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5 rounded-none bg-[#1e1a12]/65 px-3 py-1.5 backdrop-blur-sm">
                          {heroSlides.slice(0, 7).map((_, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setHeroIndex(i)}
                              className={[
                                "h-1.5 w-1.5 rounded-full transition",
                                i === heroIndex
                                  ? "bg-white"
                                  : "bg-white/40 hover:bg-white/80",
                              ].join(" ")}
                              aria-label={`Chọn ảnh ${i + 1}`}
                            />
                          ))}

                          {heroSlides.length > 7 && (
                            <span className="ml-1 text-[10px] font-semibold text-white/90">
                              +{heroSlides.length - 7}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 border-t border-[#e0d2bc] px-4 py-4">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-none bg-[#3a6665] px-4 py-2 text-sm font-bold text-white shadow-md transition hover:bg-[#2e5250]"
                    >
                      <Navigation size={16} />
                      Chỉ đường
                    </a>

                        <button
                      type="button"
                      onClick={() =>
                        locationMongoId && toggleFavorite(locationMongoId)
                      }
                      className={`inline-flex min-h-[46px] items-center justify-center gap-2 rounded-none px-4 py-2 text-sm font-bold shadow-md transition ${
                        isFav
                          ? "bg-[#6a3a4a] text-white hover:bg-[#56303e]"
                              : "bg-white text-[#8a5c38] ring-1 ring-[#d6c6a7] hover:bg-white"
                      }`}
                      title={isFav ? "Bỏ khỏi yêu thích" : "Thêm yêu thích"}
                    >
                      <Heart
                        size={16}
                        fill={isFav ? "currentColor" : "none"}
                      />
                      {isFav ? "Đã thêm" : "Thêm yêu thích"}
                    </button>
                  </div>
                </div>

                <div
                  className={`flex h-full min-h-0 flex-col p-5 ${cardClass} rounded-none`}
                >
                  <div className="flex min-h-[320px] flex-1 flex-col bg-white">
                    {(() => {
                      const rows = [
                        {
                          key: "address",
                          label: "Địa chỉ",
                          icon: MapPin,
                          visible: true,
                          value: (
                            <>
                              <div className="text-sm font-semibold text-[#2a2010]">
                                {hasFormattedAddress(location)
                                  ? formatLocationAddress(location)
                                  : "Đang cập nhật"}
                              </div>

                              {typeof location.latitude === "number" &&
                              typeof location.longitude === "number" &&
                              !Number.isNaN(location.latitude) &&
                              !Number.isNaN(location.longitude) ? (
                                <div className="mt-1 font-mono text-[11px] text-[#6a5b45]">
                                  {location.latitude.toFixed(5)},{" "}
                                  {location.longitude.toFixed(5)}
                                </div>
                              ) : null}
                            </>
                          ),
                        },
                        {
                          key: "short",
                          label: "Mô tả ngắn",
                          icon: null,
                          visible: true,
                          value: (
                            <div className="text-sm leading-7 text-[#2a2010]">
                              {(location.shortIntro || location.description || "")
                                .trim()
                                ? (location.shortIntro || "").trim() ||
                                  String(location.description || "")
                                    .replace(/<[^>]+>/g, " ")
                                    .replace(/\s+/g, " ")
                                    .trim()
                                : "Đang cập nhật"}
                            </div>
                          ),
                        },
                        {
                          key: "phone",
                          label: "Điện thoại",
                          icon: Phone,
                          visible: true,
                          value: location.phone ? (
                            <a
                              href={`tel:${location.phone}`}
                              className="text-sm font-bold text-[#2a2010] underline-offset-2 hover:underline"
                            >
                              {location.phone}
                            </a>
                          ) : (
                            <div className="text-sm font-semibold text-[#6a5b45]">
                              Đang cập nhật
                            </div>
                          ),
                        },
                        {
                          key: "hours",
                          label: "Giờ mở cửa",
                          icon: Clock,
                          visible: true,
                          value: (
                            <div className="text-sm font-bold text-[#2a2010]">
                              {location.openTime
                                ? `${location.openTime}${
                                    location.closeTime
                                      ? ` – ${location.closeTime}`
                                      : ""
                                  }`
                                : "Đang cập nhật"}
                            </div>
                          ),
                        },
                        {
                          key: "price",
                          label: "Giá",
                          icon: DollarSign,
                          visible: true,
                          value: (
                            <div className="text-sm font-bold text-[#2a2010]">
                              {(() => {
                                const hasAny =
                                  location?.priceMin != null ||
                                  location?.priceMax != null ||
                                  location?.price != null;

                                if (!hasAny) return "Đang cập nhật";

                                const min = Number(
                                  location?.priceMin ?? location?.price ?? 0
                                );
                                const max = Number(
                                  location?.priceMax ?? 0
                                );

                                if (min === 0 && max === 0) return "Miễn phí";

                                if (min === 0 && max > 0) {
                                  return `Miễn phí - ${formatVnd(max)}₫`;
                                }

                                if (min > 0 && max > 0 && min !== max) {
                                  return `${formatVnd(min)}₫ - ${formatVnd(max)}₫`;
                                }

                                const value = min || max;
                                if (value <= 0) return "Miễn phí";

                                return `${formatVnd(value)}₫`;
                              })()}
                            </div>
                          ),
                        },
                        {
                          key: "link",
                          label: "Liên kết",
                          icon: ExternalLink,
                          visible: true,
                          value: String(location.homeLinkUrl || "").trim() ? (
                            <a
                              href={String(location.homeLinkUrl).trim()}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block break-all text-sm font-bold text-[#2a2010] underline-offset-2 hover:underline"
                            >
                              {(
                                String(location.homeLinkLabel || "").trim() ||
                                "Mở liên kết"
                              ).slice(0, 120)}
                            </a>
                          ) : (
                            <div className="text-sm font-semibold text-[#6a5b45]">
                              Đang cập nhật
                            </div>
                          ),
                        },
                      ].filter((r) => r.visible);

                      const keyColWidth = "w-[148px] sm:w-[170px]";

                      return rows.map((r, i) => {
                        const Icon = r.icon;
                        const isLast = i === rows.length - 1;

                        return (
                          <div
                            key={r.key}
                            className={`flex flex-1 items-center gap-4 px-4 py-3 ${
                              isLast ? "" : "border-b border-[#3a6665]"
                            }`}
                          >
                            <div className={`${keyColWidth} shrink-0`}>
                              <div className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#3a6665]">
                                {Icon ? <Icon size={14} /> : null}
                                {r.label}
                              </div>
                            </div>

                            <div className="min-w-0 flex-1">{r.value}</div>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  {(Array.isArray(location.highlights) &&
                    location.highlights.some(Boolean)) ||
                  (Array.isArray(location.tips) &&
                    location.tips.some(Boolean)) ? (
                    <div className="mt-3 flex flex-col gap-3">
                      {Array.isArray(location.highlights) &&
                        location.highlights.filter(Boolean).length > 0 && (
                          <div className={`${panelForest} shrink-0 p-5`}>
                            <p className={strongTitleClass}>Điểm nổi bật</p>

                            <ul className="mt-3 space-y-2.5 text-sm text-white">
                              {location.highlights
                                .filter(Boolean)
                                .map((h, idx) => (
                                  <li
                                    key={`h-${idx}`}
                                    className="flex items-start gap-2.5"
                                  >
                                    <Check
                                      size={15}
                                      className="mt-[3px] shrink-0 text-white/80"
                                    />
                                    <span className="leading-7">{h}</span>
                                  </li>
                                ))}
                            </ul>
                          </div>
                        )}

                      {Array.isArray(location.tips) &&
                        location.tips.filter(Boolean).length > 0 && (
                          <div className={`${panelAmber} shrink-0 p-5`}>
                            <p className={strongTitleClass}>Mẹo hữu ích</p>

                            <ul className="mt-3 space-y-2.5 text-sm text-white">
                              {location.tips.filter(Boolean).map((t, idx) => (
                                <li
                                  key={`t-${idx}`}
                                  className="flex items-start gap-2.5"
                                >
                                  <span
                                    className="mt-[3px] shrink-0 text-sm leading-none"
                                    aria-hidden
                                  >
                                    💡
                                  </span>
                                  <span className="leading-7">{t}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </div>
                  ) : null}
                </div>
              </div>
            </section>

            {mediaBlocks.length > 0 ? (
              <section className="mx-auto w-full max-w-7xl px-7 pb-8 md:px-4 lg:max-w-[88vw]">
                <div>
                  {mediaBlocks.map((b, idx) => {
                    const key = `${b.type}-${idx}`;
                    const flip = idx % 2 === 1;

                    if (b.type === "image") {
                      const src = b.image?.url;
                      const caption = String(b.caption || "").trim();
                      const hasImg = hasUsableImageUrl(src);

                      if (!hasImg && !caption) return null;

                      return (
                        <article key={key} className="mx-auto w-full bg-white">
                          <div
                            className={[
                              "grid grid-cols-1 bg-white",
                              flip ? "md:grid-cols-[0.58fr_0.42fr]" : "md:grid-cols-[0.42fr_0.58fr]",
                              idx === 0 ? "border-t border-[#d9d9d9]" : "",
                              "border-b border-[#d9d9d9]",
                            ].join(" ")}
                          >
                            <div
                              className={[
                                "relative bg-white px-0 py-6 md:px-8 md:py-8",
                                flip
                                  ? "md:order-1 md:border-r md:border-[#d9d9d9]"
                                  : "md:order-2 md:border-l md:border-[#d9d9d9]",
                              ].join(" ")}
                            >
                              <div className="h-[300px] w-full overflow-hidden sm:h-[360px] md:aspect-[2000/1124] md:h-auto">
                                {hasImg ? (
                                  <>
                                    <img
                                      src={src}
                                      alt={caption || location.name}
                                      className="h-full w-full object-cover"
                                      loading="lazy"
                                      onError={(e) => {
                                        const img = e.currentTarget;

                                        img.onerror = null;
                                        img.style.display = "none";

                                        const parent = img.parentElement;

                                        if (parent) {
                                          parent.setAttribute("data-noimg", "1");
                                        }
                                      }}
                                    />

                                    <div className="hidden h-full w-full items-center justify-center px-6 text-center [data-noimg='1']:&flex">
                                      <span className="text-sm font-semibold text-[#6f776b]">
                                        Ảnh đang cập nhật
                                      </span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center px-6 text-center">
                                    <span className="text-sm font-semibold text-[#6f776b]">
                                      Ảnh đang cập nhật
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div
                              className={[
                                "flex min-h-full flex-col justify-center bg-white pb-10 pt-8 text-[#07112b] md:py-16",
                                flip
                                  ? "px-5 sm:px-6 md:pl-8 md:pr-4 lg:pl-10 lg:pr-6"
                                  : "px-4 sm:px-5 md:pl-4 md:pr-8 lg:pl-6 lg:pr-10",
                                flip ? "md:order-2" : "md:order-1",
                              ].join(" ")}
                            >
                              <div className="w-full max-w-[680px]">
                                <p className="m-0 whitespace-pre-line text-left text-[15px] font-normal leading-[1.9] tracking-normal text-[#07112b] md:text-[16px] md:leading-8">
                                  {caption || "Đang cập nhật"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                      ;
                    }

                    if (b.type === "video") {
                      const url = (b.videoUrl || b.video?.url || "").trim();
                      const thumb =
                        b.thumbnail?.url || b.video?.thumbnail?.url || "";
                      const missing = !url;

                      return (
                        <article
                          key={key}
                          className="mx-auto w-full bg-white"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2">
                            <button
                              type="button"
                              onClick={() =>
                                missing
                                  ? null
                                  : openVideo(url, b.title || "Video")
                              }
                              disabled={missing}
                              className={[
                                "group relative block aspect-square bg-black/10",
                                flip ? "md:order-2" : "md:order-1",
                                missing
                                  ? "cursor-not-allowed opacity-85"
                                  : "cursor-pointer",
                              ].join(" ")}
                            >
                              {missing ? (
                                <div className="flex h-full w-full items-center justify-center px-4 text-center text-[#6f776b]">
                                  <span className="rounded-lg border border-[#d8cfbf] bg-[#fffdf8]/85 px-3 py-2 text-sm font-semibold">
                                    Video đang cập nhật
                                  </span>
                                </div>
                              ) : hasUsableImageUrl(thumb) ? (
                                <img
                                  src={thumb}
                                  alt={b.title || "Video"}
                                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                  loading="lazy"
                                  onError={(e) => {
                                    const img = e.currentTarget;

                                    img.onerror = null;
                                    img.style.display = "none";
                                  }}
                                />
                              ) : (
                                <video
                                  key={url}
                                  src={url}
                                  className="h-full w-full object-cover"
                                  preload="metadata"
                                  playsInline
                                  muted
                                />
                              )}

                              {!missing ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/25 transition group-hover:bg-black/40">
                                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                                    <Play
                                      size={42}
                                      className="translate-x-0.5 text-white"
                                      fill="white"
                                    />
                                  </div>
                                </div>
                              ) : null}
                            </button>

                            <div
                              className={[
                                "flex min-h-full flex-col justify-center bg-white p-8 text-[#2a2010] sm:p-10 lg:p-12",
                                flip ? "md:order-1" : "md:order-2",
                                flip
                                  ? "md:border-r md:border-[#e7dcc3]"
                                  : "md:border-l md:border-[#e7dcc3]",
                              ].join(" ")}
                            >
                              <div className="w-full">
                                <div className="mb-5 flex items-center gap-3">
                                  <span className="h-px w-10 bg-[#c4b08a]" />
                                  <span className="text-[10px] text-[#c4b08a]">
                                    ◆
                                  </span>
                                  <span className="h-px w-10 bg-[#c4b08a]" />
                                </div>

                                {(b.title || "").trim() ? (
                                  <p className="font-faustina text-2xl font-bold text-[#2a2010]">
                                    {b.title}
                                  </p>
                                ) : null}

                                {(b.caption || "").trim() ? (
                                  <p className="mt-3 text-[15px] leading-8 text-[#5a4e38] md:text-[16px] md:leading-8">
                                    {b.caption}
                                  </p>
                                ) : (
                                  <p className="text-[15px] leading-8 text-[#5a4e38] md:text-[16px] md:leading-8">
                                    Video đang cập nhật nội dung.
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                    }

                    if (b.type === "text") {
                      const content = String(b.content || "").trim();

                      if (!content) return null;

                      return (
                        <article
                          key={key}
                          className="mx-auto border border-[#ddd0b3] bg-white p-6 text-[#2a2010] shadow-[0_12px_28px_rgba(80,58,28,0.08)] md:p-8"
                        >
                          {(b.title || "").trim() ? (
                            <p className="font-faustina text-2xl font-bold text-[#2a2010]">
                              {b.title}
                            </p>
                          ) : null}

                          <div className="prose prose-sm mt-3 max-w-none text-[#2a2010]">
                            {content.split("\n").map((line, i) => (
                              <p
                                key={i}
                                className="my-2 leading-8 text-[#2a2010]"
                              >
                                {line}
                              </p>
                            ))}
                          </div>

                          {(b.caption || "").trim() ? (
                            <p className="mt-3 text-xs text-[#6a5b45]">
                              {b.caption}
                            </p>
                          ) : null}
                        </article>
                      );
                    }

                    return null;
                  })}
                </div>
              </section>
            ) : null}

            {mediaBlocks.length === 0 ? (
              <section className="w-full px-0 pb-6">
                <div className="grid gap-6 lg:grid-cols-2">
                  <div className={`${cardClass} p-6`}>
                    <h2 className="mb-4 font-faustina text-[32px] font-bold text-[#2f352c]">
                      Mô tả chi tiết
                    </h2>

                    <div className="prose prose-sm max-w-none leading-relaxed text-[#4f5748]">
                      {location.description ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(
                              String(location.description || "").replace(/\n/g, "<br />")
                            ),
                          }}
                        />
                      ) : (
                        <p className="text-[#8a8274]">
                          Chưa có mô tả chi tiết.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className={`${cardClass} p-6`}>
                    <h2 className="mb-4 font-faustina text-[32px] font-bold text-[#2f352c]">
                      Hình ảnh
                    </h2>

                    <div className="grid grid-cols-2 gap-3">
                      {gallery.length > 0 ? (
                        gallery.slice(0, 4).map((img, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setGalleryIndex(idx + 1);
                              setGalleryModal(true);
                            }}
                            className="group relative aspect-square overflow-hidden rounded-2xl bg-[#e5d9c1]"
                          >
                            <img
                              src={img}
                              alt="Gallery"
                              className="h-full w-full object-cover transition group-hover:scale-110"
                            />

                            <div className="absolute inset-0 bg-black/0 transition group-hover:bg-black/20" />
                          </button>
                        ))
                      ) : (
                        <div className="col-span-2 flex h-32 items-center justify-center rounded-2xl bg-white text-[#8d8473]">
                          Không có hình ảnh
                        </div>
                      )}

                      {gallery.length > 4 && (
                        <button
                          type="button"
                          onClick={() => {
                            setGalleryIndex(0);
                            setGalleryModal(true);
                          }}
                          className="col-span-2 rounded-2xl border border-[#ccd8c7] bg-[#e6efe1] py-4 text-center text-sm font-bold text-[#5f745f] transition hover:bg-[#dbe8d8]"
                        >
                          Xem tất cả {gallery.length} ảnh
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            ) : null}

            {videoModal.open && (
              <div
                className="fixed inset-0 z-[120000] flex items-center justify-center bg-black/80 p-4"
                role="dialog"
                aria-modal="true"
                aria-label={videoModal.title || "Video"}
                onClick={() =>
                  setVideoModal({
                    open: false,
                    url: "",
                    title: "",
                  })
                }
              >
                <div
                  className="relative w-full max-w-4xl overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setVideoModal({
                        open: false,
                        url: "",
                        title: "",
                      })
                    }
                    className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 text-white backdrop-blur transition hover:bg-black/70"
                    aria-label="Đóng video"
                  >
                    <X size={22} />
                  </button>

                  <div className="aspect-video bg-black">
                    <video
                      key={videoModal.url}
                      src={videoModal.url}
                      className="h-full w-full"
                      controls
                      autoPlay
                      playsInline
                      preload="metadata"
                    />
                  </div>

                  {(videoModal.title || "").trim() ? (
                    <div className="border-t border-white/10 bg-black/60 px-4 py-3 text-white">
                      <p className="text-sm font-semibold">
                        {videoModal.title}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {galleryModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
                <div className="relative max-h-96 w-full max-w-3xl">
                  <button
                    type="button"
                    onClick={() => setGalleryModal(false)}
                    className="absolute -top-10 right-0 text-white transition hover:scale-110"
                  >
                    <X size={28} />
                  </button>

                  {(() => {
                    const idx = Math.min(
                      galleryIndex,
                      Math.max(0, modalSlides.length - 1)
                    );

                    const slide = modalSlides[idx];

                    if (!slide) return null;

                    if (slide.type === "video") {
                      return (
                        <div className="h-96 w-full overflow-hidden rounded-2xl bg-black">
                          <video
                            key={slide.url}
                            src={slide.url}
                            poster={slide.poster || undefined}
                            className="h-full w-full"
                            controls
                            playsInline
                            preload="metadata"
                          />
                        </div>
                      );
                    }

                    return (
                      <img
                        src={slide.url}
                        alt=""
                        className="h-96 w-full rounded-2xl object-cover"
                      />
                    );
                  })()}

                  {modalSlides.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setGalleryIndex(
                            (i) =>
                              (i - 1 + modalSlides.length) %
                              modalSlides.length
                          )
                        }
                        className="rounded-full bg-white/90 p-2 hover:bg-white"
                      >
                        <ChevronDown size={20} className="rotate-90" />
                      </button>

                      <span className="flex items-center rounded-full bg-white/90 px-4 py-2">
                        {Math.min(
                          galleryIndex,
                          Math.max(0, modalSlides.length - 1)
                        ) + 1}
                        /{modalSlides.length}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          setGalleryIndex((i) => (i + 1) % modalSlides.length)
                        }
                        className="rounded-full bg-white/90 p-2 hover:bg-white"
                      >
                        <ChevronUp size={20} className="rotate-90" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {mediaCatalogOpen && mediaCatalogSections.length > 0 ? (
              <div
                className="fixed inset-0 z-[110000] flex items-end justify-center bg-black/60 p-0 pb-4 sm:items-center sm:p-6 sm:pb-6"
                role="dialog"
                aria-modal="true"
                aria-label="Danh sách ảnh theo loại"
                onClick={() => setMediaCatalogOpen(false)}
              >
                <div
                  className="flex max-h-[min(88vh,720px)] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl border border-[#d7cab6] bg-[#fbf7ef] shadow-2xl sm:max-h-[min(85vh,calc(100vh-4.5rem))] sm:rounded-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#e0d2bc] bg-[#f7efdf] px-4 py-3 sm:px-5">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-[#7f7562]">
                        Media
                      </p>
                      <p className="text-sm font-bold text-[#2f352c]">
                        {totalCatalogImages} ảnh — theo loại
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setMediaCatalogOpen(false)}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#d7cab6] bg-[#fbf7ef] text-[#5f675b] transition hover:bg-[#f1e6d6]"
                      aria-label="Đóng"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
                    {mediaCatalogSections.map((section) => (
                      <section key={section.key} className="mb-8 last:mb-2">
                        <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-wider text-[#7f7562]">
                          {section.title}
                        </h3>

                        <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                          {section.urls.map((url, i) => (
                            <a
                              key={`${section.key}-${i}-${url}`}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group flex aspect-square max-h-[88px] items-center justify-center overflow-hidden rounded-lg border border-[#deceb6] bg-[#f3e9da] p-1 transition hover:border-[#c6d2c1] hover:bg-white sm:max-h-[100px]"
                            >
                              <img
                                src={url}
                                alt=""
                                className="max-h-full max-w-full object-contain"
                                onError={(e) => {
                                  const img = e.currentTarget;

                                  img.onerror = null;
                                  img.style.display = "none";

                                  const parent = img.parentElement;

                                  if (parent) {
                                    parent.setAttribute("data-noimg", "1");
                                  }
                                }}
                              />

                              <span className="hidden rounded-md border border-[#d8cfbf] bg-[#fffdf8]/85 px-2 py-1 text-[11px] font-semibold text-[#6f776b] [data-noimg='1']:&block">
                                Ảnh đang cập nhật
                              </span>
                            </a>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {Array.isArray(location.faq) && location.faq.length > 0 && (
              <section className="w-full px-0 pb-8">
                <div className={`${cardClass} p-6`}>
                  <h2 className="mb-4 font-faustina text-[32px] font-bold text-[#2f352c]">
                    Câu hỏi thường gặp
                  </h2>

                  <div className="space-y-3">
                    {location.faq.map((item, idx) => (
                      <div
                        key={idx}
                        className={FAQ_PANELS[idx % FAQ_PANELS.length]}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedFaq(expandedFaq === idx ? null : idx)
                          }
                          className="flex w-full items-center justify-between px-4 py-4 text-left text-sm font-black text-white transition hover:text-white/90"
                        >
                          {item.question}
                          {expandedFaq === idx ? (
                            <ChevronUp size={18} />
                          ) : (
                            <ChevronDown size={18} />
                          )}
                        </button>

                        {expandedFaq === idx && (
                          <div
                            className={`border-t border-white/15 px-4 py-4 ${strongBodyClass}`}
                          >
                            {item.answer}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default LocationDetailPage;